import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import GlAuditLink from '../components/GlAuditLink';
import {
  Search, Plus, Trash2, ClipboardList, Loader2, PackageCheck, Sparkles,
  ChevronLeft, ChevronRight, Eye, Send, X, CheckCircle2, DollarSign,
  Banknote, CreditCard, Download, Printer, Calendar, FileText, ChevronDown,
  ThumbsUp, ThumbsDown, Clock, Check
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { fmtCurrency } from '../lib/localeUtils';
import { exportToPrintablePDF } from '../lib/exportUtils';
import DirectReceiptDialog from '../components/DirectReceiptDialog';
import POPrecheckPanel, { PrecheckBadge } from '../components/POPrecheckPanel';
import ProductPickerOrCreate from '../components/ProductPickerOrCreate';
import { ExpiryDateInput } from '../components/ExpiryDateInput';
import { keyed, skeletonKeys } from '../lib/listKeys';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30' },
  pending_approval: { label: 'Pending Approval', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30' },
  approved: { label: 'Approved', color: 'bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30' },
  rejected: { label: 'Rejected', color: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30' },
  ordered: { label: 'Ordered', color: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30' },
  partial: { label: 'Partial', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  received: { label: 'Received', color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  cancelled: { label: 'Cancelled', color: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30' },
};

export default function PurchasesPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  // Create PO state
  const [createOpen, setCreateOpen] = useState(false);
  const [directReceiptOpen, setDirectReceiptOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [poItems, setPoItems] = useState([]);
  const [poNotes, setPoNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // View/Receive PO state
  const [viewPO, setViewPO] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiving, setReceiving] = useState(false);

  // Payments state
  const [activeTab, setActiveTab] = useState('orders');
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsTotalAmount, setPaymentsTotalAmount] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPages, setPaymentsPages] = useState(1);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    supplier_id: '', amount: '', payment_method: 'bank_transfer',
    reference_number: '', payment_date: new Date().toISOString().slice(0, 10), notes: '', po_ids: []
  });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentDateFrom, setPaymentDateFrom] = useState('');
  const [paymentDateTo, setPaymentDateTo] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');

  // Approval state
  const [approvalDialogPO, setApprovalDialogPO] = useState(null); // null | {po_id, action: 'approve'|'reject'|'submit'}
  const [approvalNote, setApprovalNote] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  const handleApprovalAction = async () => {
    if (!approvalDialogPO) return;
    const { po_id, action } = approvalDialogPO;
    if (action === 'reject' && !approvalNote.trim()) { toast.error('A rejection reason is required'); return; }
    setApprovalLoading(true);
    try {
      await api.post(`/purchase-orders/${po_id}/${action}`, { note: approvalNote.trim() });
      toast.success(action === 'submit' ? 'PO submitted for approval' : action === 'approve' ? 'PO approved' : 'PO rejected');
      setApprovalDialogPO(null);
      setApprovalNote('');
      fetchOrders();
      if (viewPO?.po_id === po_id) {
        const updated = await api.get(`/purchase-orders/${po_id}`);
        setViewPO(updated.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setApprovalLoading(false); }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/purchase-orders', { params: { search, status: statusFilter, page, limit: 15 } });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, 300);
    return () => clearTimeout(t);
  }, [fetchOrders]);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const res = await api.get('/supplier-payments', {
        params: {
          date_from: paymentDateFrom, date_to: paymentDateTo,
          payment_method: paymentMethodFilter, page: paymentsPage, limit: 20
        }
      });
      setPayments(res.data.payments || []);
      setPaymentsTotal(res.data.total || 0);
      setPaymentsTotalAmount(res.data.total_amount || 0);
      setPaymentsPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setPaymentsLoading(false); }
  }, [paymentDateFrom, paymentDateTo, paymentMethodFilter, paymentsPage]);

  useEffect(() => {
    if (activeTab === 'payments') fetchPayments();
  }, [activeTab, fetchPayments]);

  // Load suppliers and products for PO creation
  useEffect(() => {
    const load = async () => {
      try {
        const [supRes, prodRes] = await Promise.all([
          api.get('/suppliers', { params: { status: 'active', limit: 200 } }),
          api.get('/products', { params: { limit: 500 } })
        ]);
        setSuppliers(supRes.data.suppliers || []);
        setProducts(prodRes.data.products || []);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  // === Create PO ===
  const openCreate = () => {
    setSelectedSupplier('');
    setPoItems([]);
    setPoNotes('');
    setCreateOpen(true);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1' || !products.length) return;
    const prod = products.find(p => p.product_id === searchParams.get('product_id'));
    const qty = Math.max(1, parseInt(searchParams.get('qty'), 10) || 1);
    setSelectedSupplier(suppliers.some(s => s.supplier_id === searchParams.get('supplier_id')) ? searchParams.get('supplier_id') : '');
    setPoItems(prod ? [{ uid: crypto.randomUUID(), product_id: prod.product_id, product_name: prod.name, quantity_ordered: qty, unit_cost: prod.unit_cost ?? prod.unit_price }] : []);
    setPoNotes('');
    setCreateOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, products, suppliers]);

  const addPoItem = () => {
    setPoItems(prev => [...prev, { uid: crypto.randomUUID(), product_id: '', product_name: '', quantity_ordered: 1, unit_cost: 0 }]);
  };

  const updatePoItem = (idx, field, value) => {
    setPoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.product_id === value);
        if (prod) {
          updated.product_name = prod.name;
          updated.unit_cost = prod.unit_price;
        }
      }
      return updated;
    }));
  };

  const removePoItem = (idx) => setPoItems(prev => prev.filter((_, i) => i !== idx));

  const handleCreatePO = async () => {
    if (!selectedSupplier) { toast.error('Select a supplier'); return; }
    if (poItems.length === 0 || poItems.some(i => !i.product_id || i.quantity_ordered <= 0)) {
      toast.error('Add at least one valid item'); return;
    }
    setCreating(true);
    try {
      const supplier = suppliers.find(s => s.supplier_id === selectedSupplier);
      await api.post('/purchase-orders', {
        supplier_id: selectedSupplier,
        supplier_name: supplier?.name || '',
        items: poItems.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity_ordered: i.quantity_ordered, unit_cost: i.unit_cost })),
        notes: poNotes
      });
      toast.success('Purchase order created');
      setCreateOpen(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  // === Update Status ===
  const updateStatus = async (poId, newStatus) => {
    try {
      await api.put(`/purchase-orders/${poId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchOrders();
      if (viewPO?.po_id === poId) {
        const res = await api.get(`/purchase-orders/${poId}`);
        setViewPO(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  // === Receive Goods ===
  const openReceive = (po) => {
    setViewPO(po);
    setReceiveItems(po.items.map(item => ({
      product_id: item.product_id,
      quantity_received: Math.max(0, item.quantity_ordered - (item.quantity_received || 0)),
      batch_number: '',
      expiry_date: ''
    })));
    setReceiveOpen(true);
  };

  const handleReceive = async () => {
    if (receiveItems.some(i => Number(i.quantity_received) > 0 && i.expiry_date === null)) {
      toast.error('Check the expiry dates', { description: 'Use 03/2027, MAR 2027 or 2027-03-31' });
      return;
    }
    setReceiving(true);
    try {
      const items = receiveItems
        .filter(i => Number(i.quantity_received) > 0)
        .map(i => ({
          product_id: i.product_id || '',
          product_name: i.product_name || '',
          quantity_received: Number(i.quantity_received),
          batch_number: i.batch_number || '',
          expiry_date: i.expiry_date || '',
          add_to_po: !!i.add_to_po,
          unit_cost: i.unit_cost != null ? Number(i.unit_cost) : undefined,
          new_product: i.new_product || null,
        }));
      const res = await api.post(`/purchase-orders/${viewPO.po_id}/receive`, { items });
      const created = res.data?.auto_created_products?.length || 0;
      const unplanned = res.data?.unplanned_lines_added?.length || 0;
      let msg = res.data.status === 'received' ? 'Fully received!' : 'Partially received';
      if (unplanned) msg += ` · ${unplanned} unplanned line${unplanned === 1 ? '' : 's'} added`;
      if (created) msg += ` · ${created} new product${created === 1 ? '' : 's'} created`;
      toast.success(msg);
      setReceiveOpen(false);
      setViewPO(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Receive failed');
    } finally {
      setReceiving(false);
    }
  };

  const addUnplannedReceiveLine = (picker) => {
    if (!picker?.product_id && !picker?.new_product) return;
    setReceiveItems(prev => [...prev, {
      uid: crypto.randomUUID(),
      product_id: picker.product_id || '',
      product_name: picker.product_name || picker.new_product?.name || '',
      quantity_received: 0,
      batch_number: '',
      expiry_date: '',
      add_to_po: true,
      unit_cost: picker.unit_cost || 0,
      new_product: picker.new_product || null,
      _unplanned_ui: true,   // UI marker so we can style differently
    }]);
  };

  const deletePO = async (poId) => {
    try {
      await api.delete(`/purchase-orders/${poId}`);
      toast.success('Purchase order deleted');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const poTotal = poItems.reduce((sum, i) => sum + (i.quantity_ordered * i.unit_cost), 0);

  const handleRecordPayment = async () => {
    if (!paymentForm.supplier_id || !paymentForm.amount) {
      toast.error('Supplier and amount are required');
      return;
    }
    setPaymentSaving(true);
    try {
      await api.post('/supplier-payments', {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
      });
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentForm({
        supplier_id: '', amount: '', payment_method: 'bank_transfer',
        reference_number: '', payment_date: new Date().toISOString().slice(0, 10), notes: '', po_ids: []
      });
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally { setPaymentSaving(false); }
  };

  // === PO document exports (Print / PDF / Excel) ===
  const downloadPOAsset = async (poId, kind /* 'pdf' | 'xlsx' */) => {
    try {
      const res = await api.get(`/purchase-orders/${poId}/${kind}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${poId}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${kind.toUpperCase()} download failed`);
    }
  };

  const printPO = async (poId) => {
    try {
      const res = await api.get(`/purchase-orders/${poId}/print`, { responseType: 'text' });
      const w = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1000');
      if (!w) {
        toast.error('Please allow pop-ups so we can open the print view.');
        return;
      }
      w.document.open();
      w.document.write(res.data);
      w.document.close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open print view');
    }
  };

  const handleExportPayments = () => {
    const summaryCards = [
      { label: 'Total Payments', value: paymentsTotal },
      { label: 'Total Amount', value: fmtCurrency(paymentsTotalAmount) },
    ];
    const headers = ['Date', 'Payment ID', 'Supplier', 'Amount', 'Method', 'Reference', 'Notes'];
    const rows = payments.map(p => [
      p.payment_date, p.payment_id, p.supplier_name,
      fmtCurrency(p.amount), (p.payment_method || '').replace(/_/g, ' '),
      p.reference_number || '', p.notes || ''
    ]);
    exportToPrintablePDF({
      title: 'Supplier Payment Report',
      subtitle: paymentDateFrom || paymentDateTo
        ? `Period: ${paymentDateFrom || '...'} to ${paymentDateTo || '...'}`
        : 'All Time',
      summaryCards,
      headers,
      rows,
    });
  };

  const handleExportPaymentsCSV = () => {
    const headers = ['Date', 'Payment ID', 'Supplier', 'Amount', 'Method', 'Reference', 'Notes', 'Recorded By'];
    const csvRows = payments.map(p => [
      p.payment_date, p.payment_id, p.supplier_name,
      p.amount, (p.payment_method || '').replace(/_/g, ' '),
      p.reference_number || '', (p.notes || '').replace(/,/g, ';'),
      p.recorded_by_name || ''
    ]);
    const csvContent = [headers.join(','), ...csvRows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supplier_payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="purchases-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Procurement
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Purchase orders & supplier payments</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'orders' && (
            <>
              <Button variant="outline" onClick={() => setDirectReceiptOpen(true)} className="gap-1.5 h-10" data-testid="direct-receipt-btn">
                <PackageCheck className="h-4 w-4" /> Direct Receipt
              </Button>
              <Button onClick={openCreate} className="gap-1.5 h-10" data-testid="create-po-btn">
                <Plus className="h-4 w-4" /> New Purchase Order
              </Button>
            </>
          )}
          {activeTab === 'payments' && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1.5 h-9" data-testid="export-payments-btn">
                    <Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportPayments} data-testid="export-payments-pdf">
                    <FileText className="mr-2 h-4 w-4" /> Export PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPaymentsCSV} data-testid="export-payments-csv">
                    <Download className="mr-2 h-4 w-4" /> Export CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button className="gap-1.5 h-10" onClick={() => setPaymentDialogOpen(true)} data-testid="record-payment-btn">
                <DollarSign className="h-4 w-4" /> Record Payment
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'orders' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('orders')}
          data-testid="tab-orders"
        >
          <ClipboardList className="h-3.5 w-3.5 inline mr-1.5" />Orders ({total})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'payments' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('payments')}
          data-testid="tab-payments"
        >
          <DollarSign className="h-3.5 w-3.5 inline mr-1.5" />Payments
        </button>
      </div>

      {/* ORDERS TAB */}
      {activeTab === 'orders' && (
        <>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search PO ID or supplier..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10" data-testid="po-search" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-36" data-testid="po-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="ordered">Ordered</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card data-testid="po-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO ID</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="hidden md:table-cell text-right">Amount</TableHead>
              <TableHead className="hidden md:table-cell">Items</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              skeletonKeys(3, 'purchase-skeleton').map((key) => (
                <TableRow key={key}><TableCell colSpan={6}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              ))
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No purchase orders found</p>
                </TableCell>
              </TableRow>
            ) : orders.map(po => {
              const cfg = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;
              return (
                <TableRow key={po.po_id} data-testid={`po-row-${po.po_id}`}>
                  <TableCell className="font-mono text-sm font-medium">{po.po_id}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{po.supplier_name}</p>
                    <p className="text-xs text-muted-foreground">{po.created_at ? new Date(po.created_at).toLocaleDateString() : ''}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-right font-semibold text-sm">{fmtCurrency(po.total_amount)}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{po.items?.length || 0} items</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${cfg.color}`}>{cfg.label}</Badge>
                    <div><PrecheckBadge last={po.last_precheck} /></div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewPO(po)} data-testid={`view-po-${po.po_id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            title="Download / print PO"
                            data-testid={`po-export-${po.po_id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => printPO(po.po_id)} data-testid={`po-print-${po.po_id}`}>
                            <Printer className="h-4 w-4 mr-2" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPOAsset(po.po_id, 'pdf')} data-testid={`po-pdf-${po.po_id}`}>
                            <FileText className="h-4 w-4 mr-2" /> Download PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPOAsset(po.po_id, 'xlsx')} data-testid={`po-xlsx-${po.po_id}`}>
                            <Download className="h-4 w-4 mr-2" /> Download Excel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {po.status === 'draft' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-600" onClick={() => updateStatus(po.po_id, 'ordered')} title="Mark as Ordered" data-testid={`order-po-${po.po_id}`}>
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {(po.status === 'draft' || po.status === 'rejected') && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-600" title="Submit for Approval"
                          onClick={() => { setApprovalDialogPO({ po_id: po.po_id, action: 'submit' }); setApprovalNote(''); }}
                          data-testid={`submit-approval-${po.po_id}`}>
                          <Clock className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {po.status === 'pending_approval' && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Approve PO"
                            onClick={() => { setApprovalDialogPO({ po_id: po.po_id, action: 'approve' }); setApprovalNote(''); }}
                            data-testid={`approve-po-${po.po_id}`}>
                            <ThumbsUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" title="Reject PO"
                            onClick={() => { setApprovalDialogPO({ po_id: po.po_id, action: 'reject' }); setApprovalNote(''); }}
                            data-testid={`reject-po-${po.po_id}`}>
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {(po.status === 'ordered' || po.status === 'partial') && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => openReceive(po)} title="Receive Goods" data-testid={`receive-po-${po.po_id}`}>
                          <PackageCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {(po.status === 'draft' || po.status === 'cancelled') && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deletePO(po.po_id)} data-testid={`delete-po-${po.po_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

        </>
      )}

      {/* PAYMENTS TAB */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          {/* Payment Filters */}
          <Card>
            <CardContent className="p-3 flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1 flex-1">
                <Label className="text-xs">From</Label>
                <Input type="date" value={paymentDateFrom} onChange={e => setPaymentDateFrom(e.target.value)} className="h-9" data-testid="payment-date-from" />
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-xs">To</Label>
                <Input type="date" value={paymentDateTo} onChange={e => setPaymentDateTo(e.target.value)} className="h-9" data-testid="payment-date-to" />
              </div>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="w-44 h-9" data-testid="payment-method-filter">
                  <SelectValue placeholder="All Methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Payment Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Card>
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Total Payments</p>
                <p className="text-lg font-bold">{paymentsTotal}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Total Amount</p>
                <p className="text-lg font-bold text-primary">{fmtCurrency(paymentsTotalAmount)}</p>
              </CardContent>
            </Card>
            <Card className="hidden sm:block">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Avg Payment</p>
                <p className="text-lg font-bold">{fmtCurrency(paymentsTotal > 0 ? paymentsTotalAmount / paymentsTotal : 0)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Payments Table */}
          <Card data-testid="payments-table">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="hidden md:table-cell">Reference</TableHead>
                    <TableHead className="hidden lg:table-cell">Notes</TableHead>
                    <TableHead className="text-right w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentsLoading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p>No payments recorded</p>
                      </TableCell>
                    </TableRow>
                  ) : payments.map(p => (
                    <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`}>
                      <TableCell className="text-sm">{p.payment_date}</TableCell>
                      <TableCell className="font-medium text-sm">{p.supplier_name}</TableCell>
                      <TableCell className="text-right font-semibold text-sm">{fmtCurrency(p.amount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px] capitalize">{(p.payment_method || '').replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{p.reference_number || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-[200px] truncate">{p.notes || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          onClick={async () => {
                            try {
                              await api.delete(`/supplier-payments/${p.payment_id}`);
                              toast.success('Payment deleted');
                              fetchPayments();
                            } catch { toast.error('Delete failed'); }
                          }}
                          data-testid={`delete-payment-${p.payment_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            {paymentsPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-3 border-t">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={paymentsPage <= 1} onClick={() => setPaymentsPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground">{paymentsPage} / {paymentsPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={paymentsPage >= paymentsPages} onClick={() => setPaymentsPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md" data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle>Record Supplier Payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select value={paymentForm.supplier_id} onValueChange={v => setPaymentForm(p => ({ ...p, supplier_id: v }))}>
                <SelectTrigger data-testid="payment-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" min="0" step="0.01" data-testid="payment-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm(p => ({ ...p, payment_date: e.target.value }))} data-testid="payment-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference #</Label>
                <Input value={paymentForm.reference_number} onChange={e => setPaymentForm(p => ({ ...p, reference_number: e.target.value }))} placeholder="e.g. TXN-12345" data-testid="payment-reference-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={paymentForm.notes} onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional payment notes" rows={2} data-testid="payment-notes-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={paymentSaving} data-testid="confirm-payment-btn">
              {paymentSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create PO Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="create-po-dialog">
          <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
          <ScrollArea className="flex-1 max-h-[60vh] pr-4">
            <div className="space-y-4 pb-4">
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Order Items</Label>
                  <Button variant="outline" size="sm" onClick={addPoItem} className="h-7 text-xs gap-1" data-testid="add-po-item-btn">
                    <Plus className="h-3 w-3" /> Add Item
                  </Button>
                </div>
                {poItems.map((item, idx) => (
                  <div key={item.uid} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/50 rounded-lg" data-testid={`po-item-${idx}`}>
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Product</Label>
                      <Select value={item.product_id} onValueChange={(v) => updatePoItem(idx, 'product_id', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" value={item.quantity_ordered} onChange={(e) => updatePoItem(idx, 'quantity_ordered', parseInt(e.target.value) || 0)} min={1} className="h-8 text-xs" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Unit Cost</Label>
                      <Input type="number" value={item.unit_cost} onChange={(e) => updatePoItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)} step={0.01} className="h-8 text-xs" />
                    </div>
                    <div className="col-span-2 text-right text-sm font-semibold pt-5">
                      {fmtCurrency((item.quantity_ordered * item.unit_cost))}
                    </div>
                    <div className="col-span-1 pt-5">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removePoItem(idx)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={poNotes} onChange={(e) => setPoNotes(e.target.value)} placeholder="Additional notes..." rows={2} data-testid="po-notes-input" />
              </div>
            </div>
          </ScrollArea>
          <Separator />
          <div className="flex items-center justify-between pt-3">
            <p className="text-sm font-semibold">Total: {fmtCurrency(poTotal)}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePO} disabled={creating} data-testid="submit-po-btn">
                {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={!!viewPO && !receiveOpen} onOpenChange={() => setViewPO(null)}>
        <DialogContent className="max-w-lg" data-testid="view-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" /> {viewPO?.po_id}
            </DialogTitle>
            {viewPO?.po_id && ['received', 'partial'].includes(viewPO.status) && (
              <div className="pt-1">
                <GlAuditLink source="purchase" sourceRef={viewPO.po_id} label="View posted journal" />
              </div>
            )}
          </DialogHeader>
          {viewPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Supplier</p>
                  <p className="font-medium">{viewPO.supplier_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Status</p>
                  <Badge variant="outline" className={`text-xs ${(STATUS_CONFIG[viewPO.status] || STATUS_CONFIG.draft).color}`}>
                    {(STATUS_CONFIG[viewPO.status] || STATUS_CONFIG.draft).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Total Amount</p>
                  <p className="font-bold text-lg">{fmtCurrency(viewPO.total_amount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Created</p>
                  <p className="font-medium">{viewPO.created_at ? new Date(viewPO.created_at).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-semibold mb-2">Items</p>
                <div className="space-y-2">
                  {keyed(viewPO.items, item => item.product_id || item.product_name).map(({ item, key }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Ordered: {item.quantity_ordered} | Received: {item.quantity_received || 0} | Cost: {fmtCurrency(item.unit_cost)}
                        </p>
                      </div>
                      <p className="font-semibold">{fmtCurrency((item.quantity_ordered * item.unit_cost))}</p>
                    </div>
                  ))}
                </div>
              </div>
              {viewPO.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm">{viewPO.notes}</p>
                  </div>
                </>
              )}
              <POPrecheckPanel poId={viewPO.po_id} />
              {viewPO.approval_history?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-semibold mb-2">Approval History</p>
                    <div className="space-y-2">
                      {viewPO.approval_history.map((evt, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className={`mt-0.5 rounded-full w-2 h-2 flex-shrink-0 ${evt.action === 'approved' ? 'bg-emerald-500' : evt.action === 'rejected' ? 'bg-rose-500' : 'bg-purple-500'}`} />
                          <div>
                            <span className="font-medium capitalize">{evt.action}</span>
                            {evt.by_name && <span className="text-muted-foreground"> by {evt.by_name}</span>}
                            <span className="text-muted-foreground"> · {evt.at ? new Date(evt.at).toLocaleString() : ''}</span>
                            {evt.note && <p className="text-muted-foreground italic mt-0.5">"{evt.note}"</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => printPO(viewPO.po_id)} className="gap-1.5" data-testid="view-po-print-btn">
                  <Printer className="h-3.5 w-3.5" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPOAsset(viewPO.po_id, 'pdf')} className="gap-1.5" data-testid="view-po-pdf-btn">
                  <FileText className="h-3.5 w-3.5" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPOAsset(viewPO.po_id, 'xlsx')} className="gap-1.5" data-testid="view-po-xlsx-btn">
                  <Download className="h-3.5 w-3.5" /> Excel
                </Button>
                {(viewPO.status === 'draft' || viewPO.status === 'rejected') && (
                  <Button variant="outline" size="sm" onClick={() => { setApprovalDialogPO({ po_id: viewPO.po_id, action: 'submit' }); setApprovalNote(''); }} className="gap-1.5 text-purple-700 border-purple-300" data-testid="submit-approval-detail-btn">
                    <Clock className="h-3.5 w-3.5" /> Submit for Approval
                  </Button>
                )}
                {viewPO.status === 'pending_approval' && (
                  <>
                    <Button size="sm" onClick={() => { setApprovalDialogPO({ po_id: viewPO.po_id, action: 'approve' }); setApprovalNote(''); }} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-detail-btn">
                      <ThumbsUp className="h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setApprovalDialogPO({ po_id: viewPO.po_id, action: 'reject' }); setApprovalNote(''); }} className="gap-1.5 text-rose-600 border-rose-300" data-testid="reject-detail-btn">
                      <ThumbsDown className="h-3.5 w-3.5" /> Reject
                    </Button>
                  </>
                )}
                {viewPO.status === 'draft' && (
                  <Button variant="outline" onClick={() => updateStatus(viewPO.po_id, 'ordered')} className="gap-1.5" data-testid="mark-ordered-btn">
                    <Send className="h-3.5 w-3.5" /> Mark Ordered
                  </Button>
                )}
                {(viewPO.status === 'ordered' || viewPO.status === 'partial' || viewPO.status === 'approved') && (
                  <Button onClick={() => openReceive(viewPO)} className="gap-1.5" data-testid="receive-goods-btn">
                    <PackageCheck className="h-3.5 w-3.5" /> Receive Goods
                  </Button>
                )}
                {viewPO.status !== 'received' && viewPO.status !== 'cancelled' && (
                  <Button variant="destructive" size="sm" onClick={() => updateStatus(viewPO.po_id, 'cancelled')} data-testid="cancel-po-btn">
                    Cancel Order
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Goods Dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-w-lg" data-testid="receive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="h-4 w-4 text-emerald-600" /> Receive Goods - {viewPO?.po_id}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh]">
            <div className="space-y-3">
              {keyed(viewPO?.items, item => item.product_id || item.product_name).map(({ item, key }, idx) => {
                const remaining = item.quantity_ordered - (item.quantity_received || 0);
                if (remaining <= 0) return (
                  <div key={key} className="p-3 bg-emerald-500/10 rounded-lg flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium">{item.product_name}</span>
                    <span className="text-muted-foreground ml-auto">Fully received</span>
                  </div>
                );
                return (
                  <div key={key} className="p-3 bg-muted/50 rounded-lg space-y-2" data-testid={`receive-item-${idx}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">Remaining: {remaining}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Qty Received</Label>
                        <Input
                          type="number"
                          value={receiveItems[idx]?.quantity_received || 0}
                          onChange={(e) => {
                            const val = Math.min(parseInt(e.target.value) || 0, remaining);
                            setReceiveItems(prev => prev.map((r, i) => i === idx ? { ...r, quantity_received: val } : r));
                          }}
                          min={0} max={remaining} className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Batch #</Label>
                        <Input
                          value={receiveItems[idx]?.batch_number || ''}
                          onChange={(e) => setReceiveItems(prev => prev.map((r, i) => i === idx ? { ...r, batch_number: e.target.value } : r))}
                          className="h-8 text-xs" placeholder="e.g. B-001"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Expiry Date</Label>
                        <ExpiryDateInput
                          value={receiveItems[idx]?.expiry_date || ''}
                          onChange={(iso) => setReceiveItems(prev => prev.map((r, i) => i === idx ? { ...r, expiry_date: iso } : r))}
                          inputClassName="h-8 text-xs"
                          compact
                          testId={`receive-expiry-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Unplanned lines appended by the user via the picker below */}
              {receiveItems.map((r, idx) => {
                if (!r._unplanned_ui) return null;
                return (
                  <div key={r.uid} className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 space-y-2" data-testid={`receive-unplanned-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-emerald-800 dark:text-emerald-300">
                        <Sparkles className="h-3.5 w-3.5" /> Unplanned: {r.product_name}
                        {r.new_product && <span className="text-[10px] uppercase bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">new product</span>}
                      </div>
                      <button type="button" onClick={() => setReceiveItems(prev => prev.filter((_, i) => i !== idx))} className="text-red-600 text-xs" data-testid={`receive-unplanned-remove-${idx}`}>
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Qty Received</Label>
                        <Input type="number" min={0} value={r.quantity_received || 0}
                          onChange={(e) => setReceiveItems(prev => prev.map((x, i) => i === idx ? { ...x, quantity_received: parseInt(e.target.value) || 0 } : x))}
                          className="h-8 text-xs" data-testid={`receive-unplanned-qty-${idx}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Unit cost ₵</Label>
                        <Input type="number" step="0.01" min={0} value={r.unit_cost || 0}
                          onChange={(e) => setReceiveItems(prev => prev.map((x, i) => i === idx ? { ...x, unit_cost: parseFloat(e.target.value) || 0 } : x))}
                          className="h-8 text-xs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Batch #</Label>
                        <Input value={r.batch_number || ''}
                          onChange={(e) => setReceiveItems(prev => prev.map((x, i) => i === idx ? { ...x, batch_number: e.target.value } : x))}
                          className="h-8 text-xs" placeholder="Optional" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Expiry</Label>
                        <ExpiryDateInput
                          value={r.expiry_date || ''}
                          onChange={(iso) => setReceiveItems(prev => prev.map((x, i) => i === idx ? { ...x, expiry_date: iso } : x))}
                          inputClassName="h-8 text-xs"
                          compact
                          testId={`receive-unplanned-expiry-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add-unplanned picker — appears once at the bottom */}
              <div className="p-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 space-y-2" data-testid="receive-unplanned-picker-wrap">
                <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Add unplanned item (delivered but not on the PO)
                </div>
                <ProductPickerOrCreate
                  value={{}}
                  onChange={(sel) => addUnplannedReceiveLine(sel)}
                  placeholder="Search products or type a new name…"
                  testid="receive-unplanned-picker"
                />
              </div>
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
            <Button onClick={handleReceive} disabled={receiving} data-testid="confirm-receive-btn">
              {receiving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm Receipt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DirectReceiptDialog
        open={directReceiptOpen}
        onOpenChange={setDirectReceiptOpen}
        onCreated={() => { setDirectReceiptOpen(false); fetchOrders(); }}
      />

      {/* Approval Action Dialog */}
      <Dialog open={!!approvalDialogPO} onOpenChange={v => !v && setApprovalDialogPO(null)}>
        <DialogContent className="max-w-sm" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalDialogPO?.action === 'submit' && <><Clock className="h-4 w-4 text-purple-600" /> Submit for Approval</>}
              {approvalDialogPO?.action === 'approve' && <><ThumbsUp className="h-4 w-4 text-emerald-600" /> Approve PO</>}
              {approvalDialogPO?.action === 'reject' && <><ThumbsDown className="h-4 w-4 text-rose-600" /> Reject PO</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              PO: <span className="font-mono font-medium">{approvalDialogPO?.po_id}</span>
            </p>
            <div className="space-y-1">
              <Label className="text-sm">{approvalDialogPO?.action === 'reject' ? 'Rejection Reason *' : 'Note (optional)'}</Label>
              <Textarea
                value={approvalNote}
                onChange={e => setApprovalNote(e.target.value)}
                placeholder={approvalDialogPO?.action === 'reject' ? 'State the reason for rejection…' : 'Add a note…'}
                rows={3}
                data-testid="approval-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogPO(null)}>Cancel</Button>
            <Button
              onClick={handleApprovalAction}
              disabled={approvalLoading}
              className={approvalDialogPO?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : approvalDialogPO?.action === 'reject' ? 'bg-rose-600 hover:bg-rose-700' : ''}
              data-testid="confirm-approval-btn"
            >
              {approvalLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {approvalDialogPO?.action === 'submit' ? 'Submit' : approvalDialogPO?.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
